import { create } from 'zustand';

/**
 * How a primary tray button reveals its title on hover. `null` → follow
 * {@link DEFAULT_LABEL_REVEAL_MODE} because the user has not made an explicit
 * choice yet. Once the user picks in the Toolbar settings modal, the concrete
 * value is stored and wins.
 */
export type ToolbarLabelRevealMode = 'tooltip' | 'slideout';

/**
 * Reveal treatment used until the user picks one in the Toolbar settings modal.
 * This replaces the former `object_toolbar_button_title_slideout` flag — the
 * choice is now a user setting, so the environment only needs a fixed starting
 * default.
 */
export const DEFAULT_LABEL_REVEAL_MODE: ToolbarLabelRevealMode = 'tooltip';

export interface ObjectToolbarSettings {
  /**
   * Show the primary tray buttons' titles on hover at all. `false` → icon-only
   * buttons (the accessible name is still on each button's `aria-label`), no
   * tooltip and no slideout. Drives the "Show action labels" toggle.
   */
  showActionLabels: boolean;
  /**
   * Which reveal treatment to use when {@link showActionLabels} is on. `null`
   * means "not chosen" — the connected toolbar falls back to
   * {@link DEFAULT_LABEL_REVEAL_MODE}. Drives the tooltip/slideout row picker.
   */
  labelRevealMode: ToolbarLabelRevealMode | null;
}

export interface ObjectToolbarSettingsStore extends ObjectToolbarSettings {
  setShowActionLabels: (showActionLabels: boolean) => void;
  setLabelRevealMode: (labelRevealMode: ToolbarLabelRevealMode) => void;
}

export const DEFAULTS: ObjectToolbarSettings = {
  showActionLabels: true,
  labelRevealMode: null,
};

/**
 * Reactive, in-memory store for the object-toolbar display preferences set from
 * the "Toolbar settings" modal. A single zustand store lets the modal (which
 * writes the preference) and every mounted toolbar (which reads it) stay in
 * sync within a session.
 *
 * Session-scoped only: the store is NOT persisted, so the preferences reset to
 * {@link DEFAULTS} on each page load. Per-user persistence
 * (`user_toolbar_preferences`) is tracked as separate follow-up work and lands
 * once the toolbar ships behind its flag.
 *
 * Distinct from {@link useObjectToolbarFormStore} (the form save-state bridge):
 * that store carries transient per-page form state, this one carries the user's
 * display preferences for the session.
 */
export const useObjectToolbarSettingsStore = create<ObjectToolbarSettingsStore>(
  (set) => ({
    ...DEFAULTS,
    setShowActionLabels: (showActionLabels) => set({ showActionLabels }),
    setLabelRevealMode: (labelRevealMode) => set({ labelRevealMode }),
  })
);
